import { useCallback, useEffect, useMemo, useState } from "react";
import Menu from "../../common/components/Menu/Menu";
import { type MenuOption } from "../../common/components/Menu/MenuControlled";

import { useTranslation } from "react-i18next";
import ProjectService from "../../service/ProjectService";

import { Skeleton, Typography } from "@mui/material";
import { generatePath, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { object, string } from "yup";
import { Box, Form, InlineInput } from "../../common/components";
import { useForm } from "../../common/hooks/useForm";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import { useAppLoading } from "../../context/AppLoading/useAppLoading";
import { Styles, spacing } from "../../styles/theme";
import { Project } from "../../types";
import { useCart } from "../../utils/CartHooks";
import { ROUTES } from "../../constant/common.constant";
import { useGlobalLoading } from "../../utils/ContextHooks";
import {
  getScreenWidthMinusSpacing,
  useThemeBreakpoints,
} from "../../utils/themeUtils";
import { useAuth } from "../../common/hooks/useAuth";
import { MoveToTeamModal } from "../../components/MoveToTeamModal";
import { useTeam } from "../../context/Team";
import { useProject } from "../../context/Project/useProject";

const styles: Styles = {
  container: {
    display: "flex",
    gap: { xs: spacing.xsMobile, lg: spacing.xsDesktop },
    alignItems: "center",
  },
  title: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    marginBottom: "1px",
  },
};

const validationSchema = object({
  name: string().required("general.errors.required.input"),
});

interface Props {
  canDeleteProject: boolean;
  canRenameProject: boolean;
  project?: Project;
}

const ProjectTopSection = ({
  canDeleteProject,
  canRenameProject,
  project,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useThemeBreakpoints();
  const { isGloballyLoading } = useGlobalLoading();
  const { setIsAppLoading } = useAppLoading();
  const [editMode, setEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { fetchCartItemsAndUpdateStore } = useCart();
  const { refetchTeam, refetchTeams } = useTeam();
  const { refetchProject } = useProject();
  const [showMoveProjectModal, setShowMoveProjectModal] = useState(false);
  const { isAdmin } = useAuth();

  const formik = useForm({
    initialValues: { name: project?.name },
    onSubmit: async (values) => {
      const trimmedValue = values.name.trim();
      const res = await ProjectService.updateProjectName({
        id: project?.id ?? "",
        name: trimmedValue,
      });

      if (res.status === 200) {
        setEditMode(false);
        await Promise.all([
          refetchProject({ skipLoading: true }),
          refetchTeam({ skipLoading: true }),
          refetchTeams({ skipLoading: true }),
        ]);
        formik.setFieldValue("name", trimmedValue);
      } else {
        formik.setFieldError("name", t("project.error_occured"));
      }
    },
    validationSchema,
  });

  const handleSubmit = async () => {
    const isValid = !!formik.values.name;
    if (formik.dirty) {
      if (isValid) {
        await formik.submitForm();
      } else {
        await formik.resetForm();
      }
    }
    setEditMode(false);
  };

  const onDeleteClickClose = () => {
    setShowDeleteModal(false);
  };
  const onDelete = useCallback(async () => {
    try {
      await ProjectService.deleteProject(project?.id ?? "");

      if (project?.team_id) {
        navigate(
          generatePath(ROUTES.TEAM, {
            teamId: project?.team_id,
          })
        );
      } else {
        navigate(ROUTES.HOME);
      }
      await refetchTeam({ skipLoading: true });
      await refetchTeams({ skipLoading: true });
      await fetchCartItemsAndUpdateStore(); //Because deleting a Project and it's designs can impact the cart
    } catch (e) {
      console.error(e);
    } finally {
      setShowDeleteModal(false);
    }
  }, [
    project,
    fetchCartItemsAndUpdateStore,
    navigate,
    refetchTeam,
    refetchTeams,
  ]);

  useEffect(() => {
    formik.resetForm({ values: { name: project?.name } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.name]);

  const onTurnProjectIntoTeamClick = useCallback(async () => {
    try {
      const res = await ProjectService.turnIntoTeam(project?.id ?? "");
      await refetchTeams({ skipLoading: true });
      navigate(generatePath(ROUTES.TEAM, { teamId: res.data.data.id }));
    } catch (error: any) {
      toast.warn(
        error?.response?.data?.message ??
          "Something went wrong! Could not turn project into team"
      );
    }
  }, [project?.id, navigate, refetchTeams]);

  const onDuplicateClick = useCallback(async () => {
    try {
      setIsAppLoading(true);
      const res = await ProjectService.duplicate(project?.id ?? "");
      await refetchTeam({ skipLoading: true });
      await refetchTeams({ skipLoading: true });
      setIsAppLoading(false);
      navigate(
        generatePath(ROUTES.PROJECT, {
          projectId: res.data?.data?.id,
          teamId: project?.team_id,
        })
      );
    } catch (error: any) {
      setIsAppLoading(false);
      toast.warn(
        error?.response?.data?.message ??
          "Something went wrong! Could not duplicate"
      );
    }
  }, [
    setIsAppLoading,
    project?.id,
    project?.team_id,
    refetchTeam,
    refetchTeams,
    navigate,
  ]);

  const options: MenuOption[] = useMemo(
    () => [
      {
        labelKey: "general.rename",
        disabled: !canRenameProject,
        onClick: () => setEditMode(true),
      },
      {
        labelKey: "project.delete_menu",
        disabled: !canDeleteProject,
        onClick: () => setShowDeleteModal(true),
      },
      {
        labelKey: "project.projectIntoTeam",
        onClick: onTurnProjectIntoTeamClick,
      },
      {
        labelKey: "general.duplicate",
        onClick: onDuplicateClick,
      },
      {
        labelKey: "project.move_to_team",
        onClick: () => setShowMoveProjectModal(true),
        hideIf: !isAdmin,
        adminOnly: true,
      },
    ],
    [
      canRenameProject,
      canDeleteProject,
      onTurnProjectIntoTeamClick,
      onDuplicateClick,
      isAdmin,
    ]
  );

  return (
    <Form formik={formik}>
      <Box sx={styles.container}>
        {isGloballyLoading || !project?.name ? (
          <Skeleton sx={{ minWidth: "300px" }} />
        ) : editMode ? (
          <InlineInput
            name="name"
            autoFocus
            onBlur={handleSubmit}
            maxWidth={
              isMobile
                ? getScreenWidthMinusSpacing("16px", "16px")
                : getScreenWidthMinusSpacing("260px", "96px")
            }
            defaultValue={project.name}
            onKeyUp={(e) => e.key === "Enter" && handleSubmit()}
            inputStyle={{
              fontSize: isMobile ? 20 : 32,
              maxHeight: "unset",
              lineHeight: "normal",
            }}
            data-test="project.topSection.projectNameInput"
          />
        ) : (
          <>
            <Typography
              variant="h1"
              sx={styles.title}
              onClick={() => setEditMode(true)}
              data-test="project.topSection.projectName"
            >
              {project?.name}
            </Typography>
            <Menu
              options={options}
              iconSize={isMobile ? 22 : 32}
              disableArrowRotation
            />
          </>
        )}
        <DeleteModal
          open={showDeleteModal}
          onDelete={onDelete}
          title={t("project.delete_modal_title")}
          message={t("project.delete_modal_message")}
          onClose={onDeleteClickClose}
        />
        {!!project?.id && (
          <MoveToTeamModal
            open={showMoveProjectModal}
            onClose={() => setShowMoveProjectModal(false)}
            projectId={project?.id}
          />
        )}
      </Box>
    </Form>
  );
};

export default ProjectTopSection;
